import { useMemo, useState, type ReactNode } from "react";
import {
  Alert,
  Button,
  Card,
  Chip,
  MetricTile,
  PageContainer,
  RuntimeDiagnosticBanner,
  type ComponentDiagnostic,
} from "@swot/base-components";
import {
  FiAlertTriangle,
  FiArrowRight,
  FiCheck,
  FiCheckCircle,
  FiEdit2,
  FiEye,
  FiGitBranch,
  FiLayers,
  FiPlus,
  FiPlusCircle,
  FiRefreshCw,
  FiShield,
  FiSlash,
  FiX,
  FiZap,
} from "react-icons/fi";
import { CmtsEnterpriseModal } from "../CmtsEnterpriseModal.js";
import { CmtsSmartDataGrid, type CmtsGridColumn, type CmtsGridRowAction } from "../data-grid/index.js";
import { CmtsEnterpriseForm } from "../forms/index.js";
import { CmtsPageHintPanel } from "../hints/index.js";
import type { CmtsAdminDependencyImpact, CmtsAdminResourceConfig } from "@cmts/admin/index.js";
import { buildCmtsAdminDependencyImpact } from "@cmts/admin/index.js";
import { throwCmtsCanonicalError } from "@cmts/api/cmtsApiErrors.js";
import { isCmtsGuid, requireCmtsGuid } from "@cmts/utils/cmtsGuid.js";
import { normalizeUserSafeApiError } from "@cmts/api/apiErrorNormalizer.js";
import { CmtsRoutingRuleCustomForm } from "./CmtsRoutingRuleCustomForm.js";

export type CmtsAdminResourcePageProps = {
  config: CmtsAdminResourceConfig;
  rows?: readonly Record<string, unknown>[] | undefined;
  diagnostics?: readonly ComponentDiagnostic[] | undefined;
  busy?: boolean | undefined;
  error?: string | null | undefined;
  dependencyData?: Record<string, readonly Record<string, unknown>[]> | undefined;
  onUpsert?: ((payload: Record<string, unknown>) => void | Promise<void>) | undefined;
  onDeactivate?: ((row: Record<string, unknown>, impact?: CmtsAdminDependencyImpact) => void | Promise<void>) | undefined;
  onReactivate?: ((row: Record<string, unknown>) => void | Promise<void>) | undefined;
  onTest?: ((row: Record<string, unknown>) => void | Promise<void>) | undefined;
  onPreview?: ((row: Record<string, unknown>) => void | Promise<void>) | undefined;
  onExport?: ((rows: readonly Record<string, unknown>[]) => void | Promise<void>) | undefined;
  onRefresh?: (() => void | Promise<void>) | undefined;
};

type ActionCardProps = {
  readonly icon: ReactNode;
  readonly title: string;
  readonly description: string;
  readonly disabled?: boolean | undefined;
  readonly danger?: boolean | undefined;
  readonly onClick: () => void;
};

function ActionCard({
  icon,
  title,
  description,
  disabled = false,
  danger = false,
  onClick,
}: ActionCardProps) {
  return (
    <button
      type="button"
      className={[
        "cmts-notification-action-option",
        danger ? "cmts-notification-action-option--danger" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="cmts-notification-action-option__icon" aria-hidden="true">
        {icon}
      </span>

      <span className="cmts-notification-action-option__content">
        <strong>{title}</strong>
        <small>{description}</small>
      </span>

      <span className="cmts-notification-action-option__arrow" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

function normalizeRows(rows: readonly Record<string, unknown>[] | undefined, _kind: string): readonly Record<string, unknown>[] {
  return (rows ?? []).map((row) => ({
    ...row,
    id: isCmtsGuid(row.id) ? row.id : undefined,
    code: String(row.code ?? ""),
    label: row.label ?? row.name ?? row.schema ?? row.policy ?? row.job ?? row.title ?? row.code ?? row.id,
    status: row.status ?? (row.active === false ? "Inactive" : "Active"),
    active: row.active ?? row.status !== "Inactive",
    usageCount: row.usageCount ?? row.usedByCount ?? 0,
    dependencyStatus: row.dependencyStatus ?? (Number(row.usageCount ?? 0) > 0 ? "Used" : "Clear"),
  }));
}

async function invokeAdminHandler<TArgs extends readonly unknown[]>(
  handler: ((...args: TArgs) => void | Promise<void>) | undefined,
  actionName: string,
  ...args: TArgs
): Promise<void> {
  if (!handler) {
    throwCmtsCanonicalError({
      code: "CMTS_ERR_SIDE_EFFECT_UNAVAILABLE",
      message: `${actionName} is not connected to the backend repository adapter.`,
      details: { actionName },
    });
  }
  await handler(...args);
}

export function CmtsAdminResourcePage(props: CmtsAdminResourcePageProps) {
  const rows = useMemo(() => normalizeRows(props.rows, props.config.kind), [props.rows, props.config.kind]);
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState<Record<string, unknown> | null>(null);
  const [actionsFor, setActionsFor] = useState<Record<string, unknown> | null>(null);
  const [impactOpen, setImpactOpen] = useState(false);
  const [impactRow, setImpactRow] = useState<Record<string, unknown> | null>(null);
  const [impact, setImpact] = useState<CmtsAdminDependencyImpact | null>(null);
  const [localBusy, setLocalBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Metrics
  const totalCount = rows.length;
  const activeCount = rows.filter((r) => r.active !== false && r.status !== "Inactive").length;
  const inactiveCount = totalCount - activeCount;
  const downstreamUsageCount = rows.filter((r) => Number(r.usageCount ?? 0) > 0).length;

  const submitPayload = async (payload: Record<string, unknown>) => {
    setLocalError(null);
    setLocalBusy(true);
    try {
      const isEdit = selected !== null;
      const merged: Record<string, unknown> = {
        ...props.config.defaultValues,
        ...(isEdit ? selected : {}),
        ...payload,
        masterType: payload.masterType ?? selected?.masterType ?? props.config.defaultValues.masterType ?? props.config.kind,
        group: payload.group ?? selected?.group ?? props.config.defaultValues.group ?? props.config.defaultValues.masterType ?? props.config.kind,
        status: payload.status ?? selected?.status ?? "Active",
        active: payload.active ?? selected?.active ?? true,
      };
      if (isEdit) {
        merged.id = requireCmtsGuid(selected?.id, "Master data update");
      } else {
        delete merged.id;
      }
      await invokeAdminHandler(props.onUpsert, "Administration save", merged);
      setMessage(`${String(merged.label ?? merged.name ?? merged.code)} saved and refreshed.`);
      setFormOpen(false);
      setSelected(null);
    } catch (error) {
      setLocalError(normalizeUserSafeApiError(error).userMessage || "Administration save failed.");
    } finally {
      setLocalBusy(false);
    }
  };

  const runDependencyCheck = (row: Record<string, unknown>) => {
    const result = buildCmtsAdminDependencyImpact(row, props.dependencyData ?? {});
    setImpact(result);
    setImpactRow(row);
    setImpactOpen(true);
  };

  const refreshFromRepository = async () => {
    setLocalError(null);
    setLocalBusy(true);
    try {
      await invokeAdminHandler(props.onRefresh, "Administration refresh");
      setMessage("Grid refreshed from backend repository state.");
    } catch (error) {
      setLocalError(normalizeUserSafeApiError(error).userMessage || "Administration refresh failed.");
    } finally {
      setLocalBusy(false);
    }
  };

  const confirmDeactivate = async () => {
    if (!impactRow || !impact) return;
    if (!impact.canProceed) {
      setLocalError("Deactivate is blocked by active dependencies. Provide replacement/remediation before deactivation.");
      return;
    }
    setLocalBusy(true);
    try {
      await invokeAdminHandler(props.onDeactivate, "Administration deactivate", impactRow, impact);
      setMessage(`${String(impactRow.label ?? impactRow.code)} deactivated after dependency check.`);
      setImpactOpen(false);
      setImpactRow(null);
      setImpact(null);
    } catch (error) {
      setLocalError(normalizeUserSafeApiError(error).userMessage || "Deactivate failed.");
    } finally {
      setLocalBusy(false);
    }
  };

  // Upgraded Columns with : Actions pill button
  const columns = useMemo<readonly CmtsGridColumn<Record<string, unknown>>[]>(() => {
    return [
      ...props.config.columns,
      {
        id: "actions",
        header: "Actions",
        sortable: false,
        filterable: false,
        minWidth: 120,
        render: (row) => (
          <button
            type="button"
            className="cmts-notification-actions-button"
            aria-label={`Open actions for ${String(row.label ?? row.code)}`}
            onClick={(event) => {
              event.stopPropagation();
              setActionsFor(row);
            }}
          >
            <span className="cmts-notification-actions-button__icon" aria-hidden="true">
              ⋮
            </span>
            <span>Actions</span>
          </button>
        ),
      },
    ];
  }, [props.config.columns]);

  return (
    <PageContainer title={props.config.title} subtitle={props.config.subtitle}>
      <RuntimeDiagnosticBanner diagnostics={props.diagnostics ?? []} title={`${props.config.title} diagnostics`} />

      <CmtsPageHintPanel defaultOpen={false}>{props.config.hint}</CmtsPageHintPanel>

      {/* Hero Header */}
      <section className="cmts-workspace-hero">
        <div>
          <p className="cmts-section-eyebrow">
            {props.config.crossDomainUsage?.[0]
              ? `GOVERNANCE & ${String(props.config.crossDomainUsage[0]).toUpperCase()}`
              : "ADMINISTRATION & MASTER DATA"}
          </p>
          <h2>{props.config.title}</h2>
          <p>{props.config.subtitle}</p>
        </div>

        <div className="cmts-page-actions">
          <button
            type="button"
            className="cmts-action-button"
            onClick={() => {
              setSelected(null);
              setFormOpen(true);
            }}
          >
            <FiPlus /> {props.config.createLabel}
          </button>
          <button
            type="button"
            className="cmts-secondary-button"
            disabled={props.busy || localBusy}
            onClick={refreshFromRepository}
          >
            <FiRefreshCw className={(props.busy || localBusy) ? "cmts-spin" : ""} /> Refresh
          </button>
        </div>
      </section>

      {/* Status Messages */}
      {message ? <Alert title="Administration action completed" tone="success">{message}</Alert> : null}
      {(localError ?? props.error) ? <Alert title="Administration action blocked" tone="danger">{localError ?? props.error}</Alert> : null}

      {/* Cross Domain Usage Badge */}
      {props.config.crossDomainUsage?.length ? (
        <Card title="Cross-domain usage scope" subtitle="This admin data is reused by dropdowns, routing, reports, workflow and governance checks.">
          <div className="cmts-chip-row">
            {props.config.crossDomainUsage.map((item) => (
              <Chip label={item} key={item} tone="info" />
            ))}
          </div>
        </Card>
      ) : null}

      {/* Main Data Grid */}
      <div className="cmts-grid-wrapper" style={{ marginTop: "1rem" }}>
        <CmtsSmartDataGrid<Record<string, unknown>>
          title={props.config.title.replace(" Administration", "")}
          subtitle={`${rows.length} records available in active scope`}
          rows={rows}
          columns={columns}
          filters={props.config.filters}
          rowKey={props.config.rowKey}
          defaultPageSize={10}
          exportable
          refreshable
          loading={props.busy || localBusy}
          error={localError ?? props.error ?? null}
          onExport={async (filteredRows) => {
            try {
              setLocalError(null);
              await invokeAdminHandler(props.onExport, "Administration export", filteredRows);
              setMessage(`${filteredRows.length} scoped rows prepared for secure admin export.`);
            } catch (error) {
              setLocalError(normalizeUserSafeApiError(error).userMessage || "Administration export failed.");
            }
          }}
          onRefresh={refreshFromRepository}
          emptyMessage={props.config.emptyMessage}
        />
      </div>

      {/* Floating Action Drawer Modal */}
      <CmtsEnterpriseModal
        open={Boolean(actionsFor)}
        title="Record actions"
        subtitle={actionsFor ? `${String(actionsFor.code ?? "")} • ${String(actionsFor.label ?? actionsFor.name ?? "")}` : ""}
        size="md"
        onClose={() => setActionsFor(null)}
      >
        {actionsFor && (
          <div className="cmts-notification-action-modal">
            {/* Metadata Summary Box */}
            <div className="cmts-notification-action-modal__summary">
              <div>
                <span>Current status</span>
                <strong style={{ color: actionsFor.active ? "#059669" : "#64748b" }}>
                  {String(actionsFor.status ?? (actionsFor.active ? "ACTIVE" : "INACTIVE")).toUpperCase()}
                </strong>
              </div>

              <div>
                <span>Record Code</span>
                <code>{String(actionsFor.code ?? "-")}</code>
              </div>

              <div>
                <span>Downstream Usage</span>
                <strong>{Number(actionsFor.usageCount ?? 0) > 0 ? `${actionsFor.usageCount} references` : "No active links"}</strong>
              </div>
            </div>

            {/* Interactive Action Cards Grid (2 equal columns) */}
            <div className="cmts-notification-action-modal__grid">
              <ActionCard
                icon={<FiEdit2 />}
                title="Edit record"
                description="Update display name, metadata fields, and configuration properties."
                onClick={() => {
                  const target = actionsFor;
                  setActionsFor(null);
                  setSelected(target);
                  setFormOpen(true);
                }}
              />

              <ActionCard
                icon={<FiGitBranch />}
                title="View dependencies"
                description="Evaluate downstream entity impact and referential graph connections."
                onClick={() => {
                  const target = actionsFor;
                  setActionsFor(null);
                  runDependencyCheck(target);
                }}
              />

              {props.config.previewLabel ? (
                <ActionCard
                  icon={<FiEye />}
                  title={props.config.previewLabel}
                  description="Generate sample artifact preview and formatted schema payload."
                  onClick={async () => {
                    const target = actionsFor;
                    setActionsFor(null);
                    await invokeAdminHandler(props.onPreview, "Administration preview", target);
                    setMessage(`Preview generated for ${String(target.label ?? target.code)}.`);
                  }}
                />
              ) : null}

              {props.config.testLabel ? (
                <ActionCard
                  icon={<FiZap />}
                  title={props.config.testLabel}
                  description="Run operational simulation and contract validation."
                  onClick={async () => {
                    const target = actionsFor;
                    setActionsFor(null);
                    await invokeAdminHandler(props.onTest, "Administration test", target);
                    setMessage(`Test completed for ${String(target.label ?? target.code)}.`);
                  }}
                />
              ) : null}

              {actionsFor.active === false || actionsFor.status === "Inactive" ? (
                <ActionCard
                  icon={<FiCheck />}
                  title={props.config.reactivateLabel ?? "Reactivate record"}
                  description="Restore this record to active operational lifecycle."
                  onClick={async () => {
                    const target = actionsFor;
                    setActionsFor(null);
                    await invokeAdminHandler(props.onReactivate, "Administration reactivate", { ...target, active: true, status: "Active" });
                    setMessage(`${String(target.label ?? target.code)} reactivated.`);
                  }}
                />
              ) : null}
            </div>

            {/* Danger / Deactivation Action Card (Full-width bottom) */}
            {actionsFor.active !== false && actionsFor.status !== "Inactive" ? (
              <div className="cmts-notification-action-modal__danger">
                <ActionCard
                  icon={<FiSlash />}
                  title={props.config.deactivateLabel ?? "Deactivate record"}
                  description="Run pre-deactivation check to safely decommission this record."
                  danger={true}
                  onClick={() => {
                    const target = actionsFor;
                    setActionsFor(null);
                    runDependencyCheck(target);
                  }}
                />
              </div>
            ) : null}
          </div>
        )}
      </CmtsEnterpriseModal>

      {/* Create / Edit Form Modal */}
      <CmtsEnterpriseModal
        open={formOpen}
        title={props.config.kind === "routing" ? (selected ? "Edit Routing Rule" : "Create Routing Rule") : (selected ? props.config.editLabel : props.config.createLabel)}
        subtitle={props.config.kind === "routing" ? "Define matching trigger conditions, target queue destination, ownership and SLA policies." : "Validated repository-backed administration form"}
        size={props.config.kind === "routing" ? "xl" : "lg"}
        onClose={() => setFormOpen(false)}
      >
        {props.config.kind === "routing" ? (
          <CmtsRoutingRuleCustomForm
            initialValues={selected}
            busy={props.busy || localBusy}
            error={localError}
            isEdit={Boolean(selected)}
            submitLabel={selected ? "Save changes" : props.config.createLabel}
            onCancel={() => setFormOpen(false)}
            onSubmit={submitPayload}
          />
        ) : (
          <CmtsEnterpriseForm
            fields={props.config.fields}
            initialValues={selected ?? props.config.defaultValues}
            busy={props.busy || localBusy}
            error={localError}
            submitLabel={selected ? "Save changes" : props.config.createLabel}
            onCancel={() => setFormOpen(false)}
            onSubmit={submitPayload}
          >
            <Alert title="Repository persistence" tone="info">
              <FiShield /> Save persists through backend repository, writes audit/outbox, refreshes the grid and updates downstream dropdown/routing usage.
            </Alert>
          </CmtsEnterpriseForm>
        )}
      </CmtsEnterpriseModal>

      {/* Dependency Impact Check Modal */}
      <CmtsEnterpriseModal
        open={impactOpen}
        title="Dependency impact check"
        subtitle={impactRow ? String(impactRow.label ?? impactRow.code) : undefined}
        size="lg"
        onClose={() => setImpactOpen(false)}
      >
        {impact ? (
          <Card
            title={`Impact result: ${impact.severity}`}
            subtitle={
              impact.canProceed
                ? "No blocking active dependency was found. Deactivation can proceed."
                : "Blocking dependencies were found. Deactivation is prevented until remediation/replacement is completed."
            }
          >
            <div className="cmts-kpi-strip">
              <MetricTile label="Blocking" value={impact.blockingCount} tone={impact.blockingCount > 0 ? "danger" : "success"} />
              <MetricTile label="Warnings" value={impact.warningCount} tone={impact.warningCount > 0 ? "warning" : "success"} />
              <MetricTile label="Replacement required" value={impact.replacementRequired ? "Yes" : "No"} tone={impact.replacementRequired ? "warning" : "success"} />
            </div>
            <CmtsSmartDataGrid
              title="Impacted records"
              rows={[...impact.impactedEntities]}
              columns={[
                { id: "entityType", header: "Entity", sortable: true, filterable: true },
                { id: "entityLabel", header: "Record", sortable: true, filterable: true },
                { id: "impactType", header: "Impact", sortable: true, filterable: true },
                { id: "remediation", header: "Remediation", sortable: true, filterable: true, minWidth: 260 },
              ]}
              rowKey="entityId"
              defaultPageSize={10}
              emptyMessage="No dependency impact found."
            />
            <footer className="cmts-enterprise-form__actions" style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1rem" }}>
              <Button label="Close" variant="secondary" onClick={() => setImpactOpen(false)} />
              <Button label="Deactivate after check" variant="danger" disabled={!impact.canProceed || localBusy} onClick={confirmDeactivate} />
            </footer>
          </Card>
        ) : null}
      </CmtsEnterpriseModal>
    </PageContainer>
  );
}
